
import { useEmailBuilderStore } from '@/store/emailBuilderStore';
import { useState, useEffect } from 'react';
import { PreviewModal } from './PreviewModal';

export function BuilderToolbar() {
  const {
    document,
    isDirty,
    isSaving,
    lastSaved,
    canUndo,
    canRedo,
    undo,
    redo,
    saveDocument,
    updateDocumentMetadata,
  } = useEmailBuilderStore();

  const [documentName, setDocumentName] = useState(document?.name || '');
  const [showPreview, setShowPreview] = useState(false);

  // Re-render periodically so the relative "Saved Xm ago" text stays accurate
  const [, setStatusTick] = useState(0);
  useEffect(() => {
    const timer = setInterval(() => setStatusTick((t) => t + 1), 30000);
    return () => clearInterval(timer);
  }, []);

  // Follow the saved name when it changes (load, rename, another document). Keyed on the name,
  // not the document object, which autosave replaces every 30 s and would wipe a name being typed.
  const [syncedName, setSyncedName] = useState<string | undefined>(undefined);
  if (document && document.name !== syncedName) {
    setSyncedName(document.name);
    setDocumentName(document.name);
  }

  const handleSave = async () => {
    if (!document) return;

    try {
      // If name changed, update metadata first
      if (documentName !== document.name && documentName.trim()) {
        await updateDocumentMetadata({ name: documentName.trim() });
      }
      // Then save the document
      await saveDocument();
    } catch (error) {
      console.error('Save failed:', error);
    }
  };

  const getSaveStatus = () => {
    if (isSaving) return 'Saving...';
    if (!isDirty && lastSaved) {
      const now = new Date();
      const diff = Math.floor((now.getTime() - lastSaved.getTime()) / 1000);
      if (diff < 60) return 'Saved just now';
      if (diff < 3600) return `Saved ${Math.floor(diff / 60)}m ago`;
      return `Saved ${Math.floor(diff / 3600)}h ago`;
    }
    if (isDirty) return 'Unsaved changes';
    return 'Saved';
  };

  if (!document) {
    return (
      <div className="lw-builder-toolbar-empty flex h-14 items-center justify-between border-b border-border bg-card px-4">
        <div className="text-sm text-muted-foreground">No document loaded</div>
      </div>
    );
  }

  return (
    <div className="lw-builder-toolbar flex h-14 items-center justify-between border-b border-border bg-card px-4">
      {/* Left: Document name */}
      <div className="lw-builder-toolbar-left flex items-center gap-4">
        <input
          type="text"
          value={documentName}
          onChange={(e) => setDocumentName(e.target.value)}
          className="lw-builder-toolbar-name-input rounded-md border border-input bg-background px-3 py-1.5 text-sm font-medium text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
          placeholder="Email name"
        />
        <span className="lw-builder-toolbar-save-status text-sm text-muted-foreground">
          {getSaveStatus()}
        </span>
      </div>

      {/* Right: Actions */}
      <div className="lw-builder-toolbar-actions flex items-center gap-2">
        {/* Undo/Redo */}
        <button
          onClick={() => undo()}
          disabled={!canUndo()}
          className="lw-builder-toolbar-undo rounded-md p-2 text-muted-foreground hover:bg-accent hover:text-accent-foreground disabled:cursor-not-allowed disabled:opacity-50 transition-colors"
          title="Undo (Cmd/Ctrl+Z)"
        >
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
          </svg>
        </button>

        <button
          onClick={() => redo()}
          disabled={!canRedo()}
          className="lw-builder-toolbar-redo rounded-md p-2 text-muted-foreground hover:bg-accent hover:text-accent-foreground disabled:cursor-not-allowed disabled:opacity-50 transition-colors"
          title="Redo (Cmd/Ctrl+Shift+Z)"
        >
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 10h-10a8 8 0 00-8 8v2m18-10l-6 6m6-6l-6-6" />
          </svg>
        </button>

        <div className="mx-2 h-6 w-px bg-border" />

        {/* Preview */}
        <button
          onClick={() => setShowPreview(true)}
          className="lw-builder-toolbar-preview rounded-md bg-secondary px-3 py-2 text-sm font-medium text-secondary-foreground hover:bg-secondary/90 transition-colors"
          title="Preview email (see rendered output)"
        >
          <svg className="h-4 w-4 inline mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
          </svg>
          Preview
        </button>

        {/* Save */}
        <button
          onClick={handleSave}
          disabled={isSaving || (!isDirty && documentName === document.name)}
          className="lw-builder-toolbar-save rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50 transition-colors"
        >
          {isSaving ? 'Saving...' : 'Save'}
        </button>
      </div>

      {/* Preview Modal */}
      {showPreview && <PreviewModal onClose={() => setShowPreview(false)} />}
    </div>
  );
}
